// P-6 长代码块折叠 + 复制
//
// 阈值（Round 3 共识）：> 12 行或 > 800 字符折叠
// 折叠态：前 2 行预览 + 渐变遮罩 + "展开剩余 X 行" 胶囊
// 复制按钮：右上角，sticky；点击后变 ✓ 1.2s（不弹 toast，微交互够用）
// iOS 15.3+ 都支持 navigator.clipboard.writeText；旧版走 execCommand fallback

import { useState } from "preact/hooks";

const FOLD_THRESHOLD_LINES = 12;
const FOLD_THRESHOLD_CHARS = 800;

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    throw new Error("no clipboard api");
  } catch {
    // V2-8 残留 / Round 3 共识 fallback：旧 iOS / 非 HTTPS 走 execCommand
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.cssText = "position:fixed;top:-9999px;left:-9999px;opacity:0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

interface Props {
  lang: string;
  code: string;
}

export function CodeBlock({ lang, code }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const lines = code.split("\n");
  const tooLong = lines.length > FOLD_THRESHOLD_LINES || code.length > FOLD_THRESHOLD_CHARS;
  const showFolded = tooLong && !expanded;
  const previewLines = lines.slice(0, 2).join("\n");

  async function onCopy(e: Event) {
    e.stopPropagation();
    const ok = await copyToClipboard(code);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } else {
      // 极端情况：浏览器拒绝。简单提示，不阻断渲染
      setCopied(false);
      console.warn("[CodeBlock] copy failed");
    }
  }

  return (
    <div class="codeblock">
      <div class="codeblock__head">
        <span class="codeblock__lang">
          {lang || "text"}
          {tooLong && ` · ${lines.length} 行`}
        </span>
        <button
          class="codeblock__copy"
          onClick={onCopy}
          aria-label={copied ? "已复制" : "复制代码"}
        >
          {copied ? "✓" : "📋"}
        </button>
      </div>
      <pre class={`codeblock__pre ${showFolded ? "codeblock__pre--folded" : ""}`}>
        <code>{showFolded ? previewLines : code}</code>
        {showFolded && <div class="codeblock__fade" aria-hidden />}
      </pre>
      {tooLong && (
        <button class="codeblock__toggle" onClick={() => setExpanded(!expanded)}>
          {expanded ? "▴ 收起" : `▾ 展开剩余 ${lines.length - 2} 行`}
        </button>
      )}
    </div>
  );
}
